import React, { useMemo } from 'react';
import { useAuth } from '../components/contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import EntityTable from '../components/common/EntityTable';
import EntityModal from '../components/common/EntityModal';
import FilterPanel from '../components/common/FilterPanel';
import Pagination from '../components/common/Pagination';
import ConfirmDialog from '../components/common/ConfirmDialog';
import usePaginatedEntity from '../hooks/usePaginatedEntity';
import apiService from '../services/apiService';
import { useExportExcel } from '../hooks/useExportExcel';
import Breadcrumb from '../components/common/Breadcrumb';
import ROUTES from '../constants/routes';

/**
 * Página de gestión de tipos de documento
 *
 * Catálogo de tipos de documentos que pueden asociarse a equipos
 * (certificaciones, permisos, manuales, etc.)
 *
 * @returns {JSX.Element}
 */
function TiposDocumento() {
  const { hasPermission } = useAuth();

  const {
    data: tipos,
    rawData,
    loading,
    pagination,
    filters,
    modalOpen,
    editingItem,
    confirmDialog,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    handleCreate,
    handleEdit,
    handleDelete,
    confirmDelete,
    handleSave,
    closeModal,
    closeConfirm
  } = usePaginatedEntity({
    entityName: 'tipo de documento',
    entityNamePlural: 'tipos de documento',
    fetchFn: apiService.getTiposDocumento,
    createFn: apiService.createTipoDocumento,
    updateFn: apiService.updateTipoDocumento,
    deleteFn: apiService.deleteTipoDocumento,
    messages: {
      createSuccess: 'Tipo de documento creado',
      updateSuccess: 'Tipo de documento actualizado',
      deleteSuccess: 'Tipo de documento eliminado'
    }
  });

  const exportToExcel = useExportExcel({
    data: rawData,
    mapFn: (t) => ({ 'Código': t.codigo, 'Nombre': t.nombre, 'Descripción': t.descripcion || '' }),
    sheetName: 'TiposDocumento',
    columnWidths: [{ wch: 12 }, { wch: 25 }, { wch: 40 }],
    entityLabel: 'tipos de documento'
  });

  const columns = useMemo(() => [
    { key: 'codigo', label: 'Código', sortable: true, highlightable: true, title: true },
    { key: 'nombre', label: 'Nombre', sortable: true, highlightable: true },
    { key: 'descripcion', label: 'Descripción', highlightable: true },
    { key: 'acciones', label: 'Acciones', align: 'center' }
  ], []);

  const modalFields = useMemo(() => [
    { name: 'codigo', label: 'Código', type: 'text', required: true,
      placeholder: 'Ej: CERTGANC, PERMCIRC (8 caracteres)' },
    { name: 'nombre', label: 'Nombre', type: 'text', required: true },
    { name: 'descripcion', label: 'Descripción', type: 'textarea' }
  ], []);

  const filterConfig = useMemo(() => [
    {
      name: 'search',
      type: 'text',
      placeholder: 'Buscar por código o nombre...',
      icon: 'fa-search'
    }
  ], []);

  return (
    <>
      <Navbar />
      <div className="main-content">
        <Breadcrumb items={[
          { label: 'Inicio', to: ROUTES.HOME },
          { label: 'Tipos de Documento' }
        ]} />

        <FilterPanel
          filters={filters}
          onFilterChange={handleFilterChange}
          filterConfig={filterConfig}
          actionConfig={[
            { label: 'Exportar Excel', icon: 'fa-file-excel', variant: 'secondary', onClick: exportToExcel },
            { label: 'Nuevo Tipo', icon: 'fa-plus', variant: 'primary', onClick: handleCreate, show: () => hasPermission('tipos_documento.create') }
          ]}
        />

        <EntityTable
          columns={columns}
          data={tipos}
          loading={loading}
          searchTerm={filters.search}
          emptyMessage="No se encontraron tipos de documento"
          actions={[
            { icon: 'fa-edit',  className: 'btn-ghost-primary', title: 'Editar',   onClick: handleEdit,   show: () => hasPermission('tipos_documento.update') },
            { icon: 'fa-trash', className: 'btn-ghost-danger',  title: 'Eliminar', onClick: handleDelete, show: () => hasPermission('tipos_documento.delete') },
          ]}
          animateRows={true}
        />

        <Pagination
          currentPage={pagination.page}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleLimitChange}
          loading={loading}
        />

        <EntityModal
          isOpen={modalOpen}
          onClose={closeModal}
          onSave={handleSave}
          title={editingItem ? 'Editar Tipo de Documento' : 'Nuevo Tipo de Documento'}
          fields={modalFields}
          initialData={editingItem}
        />

        <ConfirmDialog
          isOpen={confirmDialog.open}
          onClose={closeConfirm}
          onConfirm={confirmDelete}
          title="Eliminar Tipo de Documento"
          message={`¿Está seguro de eliminar "${confirmDialog.item?.nombre}"?`}
          confirmText="Eliminar"
          variant="danger"
        />
      </div>
    </>
  );
}

export default TiposDocumento;
